import {
  AppBar,
  Card,
  CardActionArea,
  CardContent,
  InputAdornment,
  TextField,
  Toolbar,
} from '@material-ui/core';
import { AllInbox, Book, KeyboardArrowRight, Search } from '@material-ui/icons';
import {
  Color,
  ColorDynamic,
  Column,
  Columns,
  Inline,
  Stack,
  Tiles,
} from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { convertPostingPageParamsToQuery } from 'loadboard/data/dto/PostingsPageParamsDTO';
import { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDocumentTitle } from 'shared/layout/DocumentTitle';
import { WebviewLayout } from 'shared/layout/WebviewLayout';
import { useLoadboardAccess } from 'shared/modules/loadboard/core/loadboard-access/LoadboardAccessAPI';
import { LoadboardAccessBannerController } from 'shared/modules/loadboard/core/loadboard-access/LoadboardAccessBannerController';
import { LinkButton } from 'shared/routing/Links';
import { updateSearchQuery } from 'shared/utils/URLUtils';
import { trackLoadboardEvent } from '../../core/LoadboardAnalytics';
import { useLoadboardContext } from '../../data/LoadboardContext';
import {
  usePostingsCount,
  usePrivateLoadboardCount,
} from '../../data/LoadboardPostingAPI';
import { HomeScreenCard } from './HomeScreenCard';
import { HomeScreenSavedSearchList } from './HomeScreenSavedSearchList';
import { HomeScreenSuggestedLoadsSlider } from './HomeScreenSuggestedLoadsSlider';
import { LoadSuggestionsSidebarIcon } from 'shared/icons/LoadSuggestionsSidebarIcon';
import styled from 'styled-components';
import { getLoadSuggestionsPageUrl } from 'shared/modules/load-suggestions/getLoadSuggestionsPageUrl';

const TilesWrapper = styled.div`
  .SD-Tiles-child:first-child {
    flex-basis: 100%;
  }
`;

function formatLoadsCount(value: number): string {
  if (value < 5_000) {
    return value.toString();
  }

  if (value >= 10_000) {
    return '10k+';
  }

  const rounded = Math.floor(value / 100) / 10;

  return rounded % 1 < 0.5
    ? `${Math.trunc(rounded)}k+`
    : `${Math.trunc(rounded)}.5k+`;
}

function NavButton({
  count,
  onClick,
  children,
}: {
  count?: number;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardActionArea onClick={onClick}>
        <CardContent>
          <Columns align="center">
            <Column>
              <TextBox variant="body-semibold">{children}</TextBox>
            </Column>

            {count != null && (
              <Column width="content">
                <Inline verticalAlign="center">
                  <TextBox variant="body-semibold">
                    {formatLoadsCount(count)}
                  </TextBox>
                  <KeyboardArrowRight color="action" />
                </Inline>
              </Column>
            )}
          </Columns>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export function HomeScreenPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    router: { openPostingsScreen },
  } = useLoadboardContext();

  const { data: access } = useLoadboardAccess();
  const { data: postingCounts } = usePostingsCount();
  const { data: privateLoadboard } = usePrivateLoadboardCount();

  const loadSuggestionsPageUrl = getLoadSuggestionsPageUrl({
    withLeadingSlash: true,
  });

  useDocumentTitle('Super Loadboard');

  return (
    <WebviewLayout
      header={
        <AppBar position="sticky">
          <Toolbar>
            <TextField
              onClick={() => {
                navigate({
                  pathname: '/filter',
                  search: updateSearchQuery(location.search, () =>
                    convertPostingPageParamsToQuery({
                      utm_medium: 'Advanced Search',
                    }),
                  ),
                });
              }}
              fullWidth={true}
              InputProps={{
                placeholder: 'Search for Loads',
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Toolbar>
        </AppBar>
      }
    >
      <Box padding="small">
        <Stack space="small">
          <LoadboardAccessBannerController />

          <HomeScreenSuggestedLoadsSlider />

          <NavButton
            count={postingCounts?.available}
            onClick={() => {
              trackLoadboardEvent({
                name: 'SLB: Clicked Tab',
                tabName: 'available',
                tabItemCount: postingCounts?.available,
              });
              openPostingsScreen('available');
            }}
          >
            Available Loads
          </NavButton>

          {access?.is_in_private_network && (
            <NavButton
              count={privateLoadboard?.total_loads_count}
              onClick={() => {
                trackLoadboardEvent({
                  name: 'SLB: Clicked Tab',
                  tabName: 'available',
                  tabItemCount: privateLoadboard?.total_loads_count,
                });
                openPostingsScreen('private-loadboard');
              }}
            >
              Private Loadboards
            </NavButton>
          )}

          <NavButton
            count={postingCounts?.saved}
            onClick={() => {
              trackLoadboardEvent({
                name: 'SLB: Clicked Tab',
                tabName: 'saved-loads',
                tabItemCount: postingCounts?.saved,
              });
              openPostingsScreen('saved-loads');
            }}
          >
            Saved
          </NavButton>

          <TilesWrapper>
            <Tiles space={1} columns={2}>
              <HomeScreenCard
                title="Suggested Loads"
                icon={
                  <LoadSuggestionsSidebarIcon
                    htmlColor={ColorDynamic.Teal300}
                    fontSize="large"
                  />
                }
                count={postingCounts?.suggested}
                onClick={() => {
                  trackLoadboardEvent({
                    name: 'SLB: Clicked Tab',
                    tabName: 'suggested',
                    tabItemCount: postingCounts?.suggested,
                  });
                  trackLoadboardEvent({
                    name: 'Driver Opened Suggested Loads',
                    utm_medium: 'Loadboard Tab Bar',
                  });

                  navigate(loadSuggestionsPageUrl);
                }}
              />

              <HomeScreenCard
                title="Requested"
                icon={<AllInbox htmlColor={Color.Yellow300} fontSize="large" />}
                count={postingCounts?.requested}
                onClick={() => {
                  trackLoadboardEvent({
                    name: 'SLB: Clicked Tab',
                    tabName: 'requested',
                    tabItemCount: postingCounts?.requested,
                  });
                  openPostingsScreen('requested');
                }}
              />

              <HomeScreenCard
                title="Booked"
                icon={<Book htmlColor={Color.Green300} fontSize="large" />}
                count={postingCounts?.booked}
                onClick={() => {
                  trackLoadboardEvent({
                    name: 'SLB: Clicked Tab',
                    tabName: 'booked',
                    tabItemCount: postingCounts?.booked,
                  });
                  openPostingsScreen('booked');
                }}
              />
            </Tiles>
          </TilesWrapper>

          <HomeScreenSavedSearchList />

          {import.meta.env.VITE_APP_TARGET !== 'production' && (
            <LinkButton to="/test/mobile-methods" fullWidth={true}>
              Mobile methods
            </LinkButton>
          )}
        </Stack>
      </Box>
    </WebviewLayout>
  );
}
